/**
 * API functions for interacting with cases data
 */
import { db, isFirebaseConfigured } from '../../../lib/firebase';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  where, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { COLLECTIONS } from '../../../lib/database.types';
import { Case, NewCaseData, UpdateCaseData, CaseFinancials } from '../types';

/**
 * Fetch all cases from the database
 * @returns Promise resolving to an array of cases
 */
export async function fetchCases() {
  if (!isFirebaseConfigured) {
    return { data: [], error: null };
  }

  try {
    const casesQuery = query(
      collection(db, COLLECTIONS.CASES),
      orderBy('created_at', 'desc')
    );
    
    const casesSnapshot = await getDocs(casesQuery);
    const cases = casesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Mock document count for now
      documents: { count: 0 }
    }));

    return { data: cases, error: null };
  } catch (error: any) {
    console.error('Error fetching cases:', error);
    return { data: [], error: error.message };
  }
}

/**
 * Fetch a single case by ID
 * @param id The case ID to fetch
 * @returns Promise resolving to a case or null if not found
 */
export async function fetchCaseById(id: string): Promise<Case | null> {
  if (!isFirebaseConfigured) {
    return null;
  }

  try {
    const caseDocRef = doc(db, COLLECTIONS.CASES, id);
    const caseDoc = await getDoc(caseDocRef);
    
    if (!caseDoc.exists()) {
      return null;
    }

    const caseData = { id: caseDoc.id, ...caseDoc.data() } as Case;
    return {
      ...caseData,
      case_number: caseData.reference_number || `CASE-${caseData.id.substring(0, 6)}`,
      documents_count: caseData.documents?.count || 0
    };
  } catch (error) {
    console.error('Error fetching case by ID:', error);
    return null;
  }
}

/**
 * Create a new case
 * @param caseData The data for the new case
 * @returns Promise resolving to the created case
 */
export async function createCase(caseData: NewCaseData): Promise<Case> {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured');
  }

  try {
    const caseDocRef = await addDoc(collection(db, COLLECTIONS.CASES), {
      ...caseData,
      created_at: serverTimestamp(),
      last_modified: serverTimestamp()
    });
    
    const caseDataWithId = { 
      ...caseData, 
      id: caseDocRef.id,
      created_at: new Date(),
      last_modified: new Date()
    };
    
    return {
      ...caseDataWithId,
      case_number: caseDataWithId.reference_number || `CASE-${caseDataWithId.id.substring(0, 6)}`,
      documents_count: 0
    };
  } catch (error) {
    console.error('Error creating case:', error);
    throw error;
  }
}

/**
 * Update an existing case
 * @param id The ID of the case to update
 * @param caseData The data to update
 * @returns Promise resolving to the updated case
 */
export async function updateCase(id: string, caseData: UpdateCaseData): Promise<Case> {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured');
  }

  try {
    const caseDocRef = doc(db, COLLECTIONS.CASES, id);
    await updateDoc(caseDocRef, {
      ...caseData,
      last_modified: serverTimestamp()
    });
    
    const caseDataWithId = { 
      ...caseData, 
      id,
      created_at: new Date(),
      last_modified: new Date()
    };
    
    return {
      ...caseDataWithId,
      case_number: caseDataWithId.reference_number || `CASE-${caseDataWithId.id.substring(0, 6)}`,
      documents_count: caseDataWithId.documents?.count || 0
    };
  } catch (error) {
    console.error('Error updating case:', error);
    throw error;
  }
}

/**
 * Delete a case by ID
 * @param id The ID of the case to delete
 * @returns Promise resolving to void
 */
export async function deleteCase(id: string): Promise<void> {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured');
  }

  try {
    const caseDocRef = doc(db, COLLECTIONS.CASES, id);
    await deleteDoc(caseDocRef);
  } catch (error) {
    console.error('Error deleting case:', error);
    throw error;
  }
}

/**
 * Assign a bike to a case
 * @param caseId The ID of the case
 * @param bikeId The ID of the bike to assign
 * @returns Promise resolving to the updated case
 */
export async function assignBikeToCaseApi(caseId: string, bikeId: string): Promise<Case> {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured');
  }

  try {
    const caseDocRef = doc(db, COLLECTIONS.CASES, caseId);
    await updateDoc(caseDocRef, { 
      assigned_bike_id: bikeId,
      last_modified: serverTimestamp()
    });
    
    const caseData = await fetchCaseById(caseId);
    if (caseData) {
      return {
        ...caseData,
        case_number: caseData.reference_number || `CASE-${caseData.id.substring(0, 6)}`,
        documents_count: caseData.documents?.count || 0
      };
    }
    throw new Error('Case not found');
  } catch (error) {
    console.error('Error assigning bike to case:', error);
    throw error;
  }
}

/**
 * Fetch financial details for a case
 * @param caseId The ID of the case
 * @returns Promise resolving to financial details
 */
export async function fetchCaseFinancials(caseId: string): Promise<CaseFinancials> {
  if (!isFirebaseConfigured) {
    return {
      caseId,
      invoicedAmount: 0,
      reserveAmount: 0,
      settlementAmount: 0,
      paidAmount: 0
    };
  }

  try {
    // Add CASE_FINANCIALS to collections if it doesn't exist
    const financialsQuery = query(
      collection(db, 'case_financials'), 
      where('case_id', '==', caseId)
    );
    const financialsSnapshot = await getDocs(financialsQuery);
    
    if (financialsSnapshot.empty) {
      return {
        caseId,
        invoicedAmount: 0,
        reserveAmount: 0,
        settlementAmount: 0,
        paidAmount: 0
      };
    }

    const caseFinancials = financialsSnapshot.docs[0].data() as CaseFinancials;
    return caseFinancials;
  } catch (error) {
    console.error('Error fetching case financials:', error);
    throw error;
  }
}

/**
 * Fetch financial details for a case
 * @param caseId The ID of the case
 * @returns Promise resolving to financial details
 */
export async function fetchFinancialDetails(caseId: string): Promise<any> {
  // Placeholder function to match component imports
  return {
    invoiced_amount: 0,
    reserve_amount: 0,
    settlement_amount: 0,
    paid_amount: 0
  };
}

/**
 * Update financial details for a case
 * @param caseId The ID of the case
 * @param data The financial data to update
 * @returns Promise resolving to void
 */
export async function updateFinancialDetails(caseId: string, data: any): Promise<void> {
  // Placeholder function to match component imports
  console.log('Updating financial details for case:', caseId, data);
}

/**
 * Assign a bike to a case (alias for component compatibility)
 * @param caseId The ID of the case
 * @param bikeId The ID of the bike to assign
 * @returns Promise resolving to the updated case
 */
export async function assignBikeToCase(caseId: string, bikeId: string): Promise<Case> {
  return assignBikeToCaseApi(caseId, bikeId);
}

/**
 * Upload a document to a case
 * @param caseId The ID of the case
 * @param file The file to upload
 * @returns Promise resolving to void
 */
export async function uploadDocumentToCase(caseId: string, file: File): Promise<void> {
  // Placeholder function to match component imports
  console.log('Uploading document for case:', caseId, file.name);
}

/**
 * Fetch documents for a case
 * @param caseId The ID of the case
 * @returns Promise resolving to documents array
 */
export async function fetchCaseDocuments(caseId: string): Promise<any[]> {
  // Placeholder function to match component imports
  return [];
}

/**
 * Delete a document
 * @param documentId The ID of the document to delete
 * @returns Promise resolving to void
 */
export async function deleteDocument(documentId: string): Promise<void> {
  // Placeholder function to match component imports
  console.log('Deleting document:', documentId);
}
